// Vastu Shastra directional data
// Compass convention: 0° = North, clockwise (0° North → 90° East → 180° South → 270° West)

export const CARDINAL_8 = [
  { code: "N",  name: "North",      sanskrit: "Uttara",   deity: "Kubera",   element: "Water",   guidance: "Wealth, prosperity, and career growth. Ideal for treasury, safe, and study.", color: "#10B981" },
  { code: "NE", name: "North-East", sanskrit: "Ishanya",  deity: "Ishana",   element: "Water",   guidance: "Most auspicious zone. Best for puja room, water storage, and open space.", color: "#06B6D4" },
  { code: "E",  name: "East",       sanskrit: "Purva",    deity: "Indra",    element: "Air",     guidance: "Health, family bonding, and social status. Ideal for entrance and living area.", color: "#F59E0B" },
  { code: "SE", name: "South-East", sanskrit: "Agneya",   deity: "Agni",     element: "Fire",    guidance: "Zone of fire. Ideal for kitchen, electrical equipment, and generators.", color: "#EF4444" },
  { code: "S",  name: "South",      sanskrit: "Dakshina", deity: "Yama",     element: "Earth",   guidance: "Fame, stability, and endurance. Suitable for storage and heavy items.", color: "#DC2626" },
  { code: "SW", name: "South-West", sanskrit: "Nairitya", deity: "Nirriti",  element: "Earth",   guidance: "Zone of stability. Ideal for master bedroom and heavy furniture.", color: "#D97706" },
  { code: "W",  name: "West",       sanskrit: "Paschima", deity: "Varuna",   element: "Water",   guidance: "Knowledge, learning, and gains. Suitable for children's bedroom and dining.", color: "#3B82F6" },
  { code: "NW", name: "North-West", sanskrit: "Vayavya",  deity: "Vayu",     element: "Air",     guidance: "Zone of air. Ideal for guest bedroom, garage, and grain storage.", color: "#8B5CF6" },
];

// 16-direction (secondary) labels - between the 8 cardinals
export const CARDINAL_16 = [
  "N", "NNE", "NE", "ENE",
  "E", "ESE", "SE", "SSE",
  "S", "SSW", "SW", "WSW",
  "W", "WNW", "NW", "NNW",
];

// 32-division: standard Vastu 32-entrance (pada) chart.
// Each cardinal group is centred on its cardinal; N1 starts at 326.25°, N8 ends at 56.25°, then E1..E8, S1..S8, W1..W8.
// Sector edges fall on multiples of 11.25° (so 0°, 90°, 180°, 270° are boundaries).
export const CARDINAL_32 = Array.from({ length: 32 }, (_, i) => {
  const k = (i + 3) % 32; // sector 0 (0°–11.25°) is N4
  return `${"NESW"[Math.floor(k / 8)]}${(k % 8) + 1}`;
});

const PADA_RE = /^([NESW])([1-8])$/;

// Compass centre angle for a 32-pada code (N1 → 331.875°, N4 → 5.625°, E1 → 61.875° …)
export const padaCenterDeg = (code) => {
  const m = PADA_RE.exec(code);
  if (!m) return null;
  const base = { N: 0, E: 90, S: 180, W: 270 }[m[1]];
  // Group spans base-33.75° → base+56.25° (e.g. N1 = 326.25°–337.5°, N4 = 0°–11.25°)
  return (base - 33.75 + (Number(m[2]) - 1) * 11.25 + 5.625 + 360) % 360;
};

// Get the parent 8-cardinal for any 16 or 32 label
export const CARDINAL_TO_PARENT_8 = (code) => {
  const pada = padaCenterDeg(code);
  if (pada !== null) return CARDINAL_8[Math.floor(((pada + 22.5) % 360) / 45)].code;
  const c = code.toUpperCase();
  if (c.startsWith("NE") || c === "NNE" || c === "ENE") return "NE";
  if (c.startsWith("SE") || c === "ESE" || c === "SSE") return "SE";
  if (c.startsWith("SW") || c === "SSW" || c === "WSW") return "SW";
  if (c.startsWith("NW") || c === "NNW" || c === "WNW") return "NW";
  if (c === "N" || c === "E" || c === "S" || c === "W") return c;
  return "N";
};

export const getCardinalSet = (count) => {
  if (count === 8) return CARDINAL_8.map(c => c.code);
  if (count === 16) return CARDINAL_16;
  return CARDINAL_32;
};

// Authentic MahaVastu 16-zone colour wheel (Water → Air/Wood → Fire → Earth → Space).
// Each of the 16 directions carries the colour that balances its elemental energy.
export const ZONE_COLORS_16 = {
  N:   "#2563EB", // Water — blue
  NNE: "#38BDF8", // Water — light blue
  NE:  "#93C5FD", // Water — pale blue / silver-white
  ENE: "#2DD4BF", // Air/Wood — teal (blue-green)
  E:   "#16A34A", // Air/Wood — green
  ESE: "#A3E635", // Air/Wood — light green
  SE:  "#FB923C", // Fire — orange
  SSE: "#FB7185", // Fire — pink/red
  S:   "#DC2626", // Fire — red
  SSW: "#FACC15", // Earth — yellow
  SW:  "#CA8A04", // Earth — golden
  WSW: "#FDE68A", // Space — pale yellow / white
  W:   "#94A3B8", // Space — silver
  WNW: "#9CA3AF", // Space — grey
  NW:  "#D6D3D1", // Space — cream
  NNW: "#BAE6FD", // Water — light blue
};

// Zone colour for any 8 / 16 / 32 code, following Vastu colour principles for each direction.
export const getZoneColor = (code) => {
  if (ZONE_COLORS_16[code]) return ZONE_COLORS_16[code];
  const deg = padaCenterDeg(code); // 32-pada: map to its containing 22.5° zone
  if (deg !== null) {
    const idx = Math.round(deg / 22.5) % 16;
    return ZONE_COLORS_16[CARDINAL_16[idx]];
  }
  return ZONE_COLORS_16.N;
};

export const getVastuInfo = (code) => {
  const parent = CARDINAL_TO_PARENT_8(code);
  return CARDINAL_8.find(c => c.code === parent) || CARDINAL_8[0];
};
